import {addUrlToHistoryStack} from '@github-ui/history'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useCallback, useSyncExternalStore} from 'react'

import {EMBEDDED_COMMENT_SEARCH_PARAM} from '../utils/urls'

const subscribe = (onHashChange: () => void) => {
  window.addEventListener('hashchange', onHashChange)
  window.addEventListener('popstate', onHashChange)
  window.addEventListener('statechange', onHashChange)
  return () => {
    window.removeEventListener('hashchange', onHashChange)
    window.removeEventListener('popstate', onHashChange)
    window.removeEventListener('statechange', onHashChange)
  }
}

const getHash = () => ssrSafeLocation.hash
const getServerHash = () => ''

export const useHash = (commentIssueUrl?: string) => {
  const getScopedHash = useCallback(() => {
    if (!commentIssueUrl) return getHash()
    const currentUrl = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
    const commentUrl = currentUrl.searchParams.get(EMBEDDED_COMMENT_SEARCH_PARAM)
    if (commentUrl && URL.canParse(commentUrl, currentUrl)) {
      const target = new URL(commentUrl, currentUrl)
      const issue = new URL(commentIssueUrl, currentUrl)
      if (
        target.origin === issue.origin &&
        target.pathname === issue.pathname &&
        /^#issuecomment-\d+$/.test(target.hash)
      ) {
        return target.hash
      }
    }
    // Existing event permalinks remain unchanged, but a host comment belongs to the host issue.
    return getHash().startsWith('#issuecomment-') ? '' : getHash()
  }, [commentIssueUrl])
  const hash = useSyncExternalStore(subscribe, getScopedHash, getServerHash)

  const updateHash = useCallback(
    (newHash: string) => {
      if (newHash === hash) return
      if (commentIssueUrl) {
        const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
        const target = new URL(commentIssueUrl, url)
        target.hash = newHash
        url.searchParams.set(EMBEDDED_COMMENT_SEARCH_PARAM, target.toString())
        addUrlToHistoryStack(url.toString())
      } else {
        ssrSafeLocation.hash = newHash
      }
    },
    [commentIssueUrl, hash],
  )

  return [hash, updateHash, getScopedHash] as const
}
